import styled from '@emotion/styled';
import { colors, fonts, layout, radii } from './theme';

const HeroRoot = styled.section`
  background: ${colors.coreBlue};
  padding: 0 clamp(20px, 4vw, 32px);
`;

const HeroInner = styled.div`
  max-width: ${layout.maxWidth};
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  min-height: 560px;

  @media (min-width: 900px) {
    grid-template-columns: 1.1fr 0.9fr;
    align-items: end;
  }
`;

const TextColumn = styled.div`
  padding: clamp(56px, 8vw, 88px) 0;
`;

const Eyebrow = styled.p`
  font-family: ${fonts.mono};
  font-size: 13px;
  letter-spacing: 0.08em;
  color: ${colors.signalBlue};
  text-transform: uppercase;
  margin: 0 0 24px;
`;

const Headline = styled.h1`
  font-family: ${fonts.sans};
  font-size: clamp(46px, 6.4vw, 74px);
  line-height: 1.06;
  letter-spacing: -0.03em;
  max-width: 17ch;
  font-weight: 400;
  color: ${colors.baselineWhite};
  margin: 0 0 24px;
`;

const AccentLine = styled.span`
  display: block;
  font-family: ${fonts.mono};
  color: ${colors.signalBlue};
`;

const Subcopy = styled.p`
  font-family: ${fonts.sans};
  font-size: 20px;
  line-height: 1.4;
  color: #a8c4d6;
  max-width: 52ch;
  margin: 0 0 40px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const PrimaryButton = styled.a`
  box-sizing: border-box;
  font-family: ${fonts.sans};
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.01em;
  font-size: 18px;
  padding: 13px 24px;
  height: 52px;
  gap: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.actionAccentFg};
  background: ${colors.actionAccentBg};
  border: 1px solid transparent;
  border-radius: ${radii.sm};
  text-decoration: none;

  &:hover {
    background: ${colors.actionAccentBgHover};
  }
`;

const SecondaryButton = styled.a`
  box-sizing: border-box;
  font-family: ${fonts.sans};
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.01em;
  font-size: 18px;
  padding: 13px 24px;
  height: 52px;
  gap: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.baselineWhite};
  background: transparent;
  border: 1px solid ${colors.borderDefault};
  border-radius: ${radii.sm};
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const VisualWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 470px;
  margin: 0 0 clamp(56px, 8vw, 88px);
  isolation: isolate;

  @media (min-width: 900px) {
    justify-self: end;
  }
`;

const PhoneBackdrop = styled.div`
  position: absolute;
  right: 22px;
  top: -56px;
  width: 50%;
  height: 64%;
  z-index: 0;
  display: flex;
  flex-direction: column;
`;

const Band = styled.div<{ flexValue: number; background: string }>`
  flex: ${({ flexValue }) => flexValue};
  background: ${({ background }) => background};
`;

const BACKDROP_BANDS = [
  { flexValue: 3, background: '#152B3E' },
  { flexValue: 2.2, background: '#17334A' },
  { flexValue: 1.6, background: colors.patternBlue4 },
  { flexValue: 1.3, background: colors.markCyan400 },
  { flexValue: 1.4, background: colors.markCyan200 },
  { flexValue: 1.5, background: colors.patternBlue1 },
];

const DeviceRow = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const CtvPanel = styled.div`
  box-sizing: border-box;
  position: relative;
  z-index: 2;
  flex: 1;
  min-width: 0;
  background: #0b1522;
  border: 1px solid ${colors.borderInverse};
  border-radius: 14px;
  padding: 10px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
`;

const CtvSlot = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CtvVideo = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 6px;
`;

const CtvLabel = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  font-family: ${fonts.mono};
  font-size: 9px;
  letter-spacing: 0.08em;
  color: ${colors.signalBlue};
  background: rgba(11, 21, 34, 0.82);
  padding: 3px 6px;
  pointer-events: none;
`;

const CtvHandle = styled.div`
  margin: 10px auto 2px;
  width: 56px;
  height: 3px;
  background: rgba(255, 255, 255, 0.25);
`;

const PhoneFrame = styled.div`
  box-sizing: border-box;
  position: relative;
  flex: none;
  width: 40%;
  max-width: 176px;
  margin-left: -7%;
  aspect-ratio: 9 / 16;
  border-radius: 20px;
  overflow: hidden;
  border: 7px solid #0b1522;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const PhoneVideo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// 10.625% clears the "next video" white footer baked into the GIF itself
// (68px of its 640px height) so the label sits on the video, not the footer.
const PhoneLabel = styled.span`
  position: absolute;
  bottom: calc(10.625% + 6px);
  right: 8px;
  font-family: ${fonts.mono};
  font-size: 7px;
  letter-spacing: 0.06em;
  color: ${colors.signalBlue};
  background: rgba(11, 21, 34, 0.82);
  padding: 2px 5px;
  pointer-events: none;
`;

export default function Hero() {
  return (
    <HeroRoot>
      <HeroInner>
        <TextColumn>
          <Eyebrow>Pixels&apos; Contextual Video Stream</Eyebrow>
          <Headline>
            contextual video stream for
            <AccentLine>publisher supply</AccentLine>
          </Headline>
          <Subcopy>
            AI-powered contextual video stream. Frame-by-frame understanding of the video,
            high-speed understanding of the page, streamed to the most relevant existing web,
            app and CTV placements.
          </Subcopy>
          <Actions>
            <PrimaryButton href="/contact/">Book a demo</PrimaryButton>
            <SecondaryButton href="#player">See the player</SecondaryButton>
          </Actions>
        </TextColumn>
        <VisualWrapper>
          <PhoneBackdrop>
            {BACKDROP_BANDS.map((band) => (
              <Band key={band.background} flexValue={band.flexValue} background={band.background} />
            ))}
          </PhoneBackdrop>
          <DeviceRow>
            <CtvPanel>
              <CtvSlot>
                <CtvVideo src="/hero/ua-ctv-480x270.webp" alt="Under Armour CTV video ad example" />
                <CtvLabel>PIXELS STREAM &middot; VAST</CtvLabel>
              </CtvSlot>
              <CtvHandle />
            </CtvPanel>
            <PhoneFrame>
              <PhoneVideo
                src="/hero/ua-mobile-spain-360x640.gif"
                alt="Under Armour mobile video ad example"
              />
              <PhoneLabel>PIXELS STREAM &middot; HLS</PhoneLabel>
            </PhoneFrame>
          </DeviceRow>
        </VisualWrapper>
      </HeroInner>
    </HeroRoot>
  );
}
